import type {
  AddBookingAPIResponse,
  AddBookingPayload,
  BookingsOverviewAPIResponse,
  DiscountsAPIResponse,
  VouchersAPIResponse,
} from './types'
import defaultFetch from 'isomorphic-unfetch'
import { BookingsOverviewData, Discount, Voucher } from '../domain/types'
import { bookingsOverviewMapper } from './mappers/bookingsOverviewMapper'
import { mapDiscount } from './mappers/discountMapper'
import { mapVouchers } from './mappers/vouchersMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
}

export function createBookingsOverviewAPIClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
  } = dependencies as Dependencies

  async function getBookingsOverviewData(contextItemId: string): Promise<BookingsOverviewData> {
    if (!contextItemId)
      throw new Error('contextItemId is required')
    try {
      const queryString = `?${new URLSearchParams({ contextItemId }).toString()}`
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_BASE_URL}/bookingApi/getself${queryString}`,
      )
      const data: BookingsOverviewAPIResponse = await response.json()
      return bookingsOverviewMapper(data)
    }
    catch {
      throw new Error('Failed to fetch bookings overview data')
    }
  }

  async function getDiscountData(): Promise<Discount | null> {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_BASE_URL}/customerApi/getCustomerDiscounts`,
      )
      const data: DiscountsAPIResponse = await response.json()
      return mapDiscount(data)
    }
    catch {
      throw new Error('Failed to fetch discount data')
    }
  }

  async function addBooking(payload: AddBookingPayload): Promise<AddBookingAPIResponse> {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_BASE_URL}/bookingApi/LinkParticipantToBooking`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingNumber: payload.bookingNumber,
            lastName: payload.lastName,
            departureDate: payload.departureDate,
          }),
        },
      )

      if (!response.ok) {
        throw new Error('Failed to link participant to booking')
      }

      const data: AddBookingAPIResponse = await response.json()
      return data
    }
    catch {
      throw new Error('Failed to link participant to booking')
    }
  }

  async function getVouchers(): Promise<Record<string, Voucher>> {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_BASE_URL}/VoucherApi/GetCustomerVouchers`,
      )
      const data: VouchersAPIResponse = await response.json()
      return mapVouchers(data)
    }
    catch {
      throw new Error('Failed to fetch vouchers')
    }
  }

  return {
    getBookingsOverviewData,
    addBooking,
    getDiscountData,
    getVouchers,
  }
}

export default createBookingsOverviewAPIClient()
